import TextBoxActionsMenu from './TextBoxActionsMenu';
import { resolveBoxPreset } from '../../../utils/privateLayouts';
import { useState } from 'react';
import {
  Download,
  ExternalLink,
  Image as ImageIcon,
  Plus,
  Save,
  Square,
  Type,
} from 'lucide-react';
import { WorkflowTabs } from '../../../components/PresetEditorWorkflow';
import { getTextBoxColor } from '../model/textBoxColors';

export default function WorkflowSidebar({ editor }) {
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const {
    activeWorkflowTab,
    setActiveWorkflowTab,
    designPresets,
    setSelectedId,
    setSelectedTextItemId,
    selectedTextItemId,
    addMainTextToPage,
    activeMainTextVisibility,
    addingMainText,
    pageTextEntries,
    renamePageTextEntry,
    openAdditionalContentModal,
    imageUploadRef,
    handleImageUpload,
    selectedVectorLayoutId,
    handleVectorLayoutSelection,
    vectorLayouts,
    applyVectorLayout,
    pageData,
    exportToPDF,
    savePage,
    saveLayout,
    savingTextItems,
    toggleLayoutBoxVisibility,
    savingPage,
    renderingPreview,
    layoutTemplates,
    selectedLayoutTemplateId,
    setSelectedLayoutTemplateId,
    loadingLayoutTemplates,
    layoutTemplatesError,
    savingLayoutTemplate,
    copyingLayoutTemplate,
    saveLayoutTemplate,
    copyLayoutTemplate,
  } = editor;

  const boxes = editor.textItems || [];
  const savedTextBoxes = boxes.filter((item) => item.role !== 'main' && item.sourceType !== 'page_main');
  const savedMainText = boxes.find((item) => item.role === 'main' || item.sourceType === 'page_main');
  const sharedTextBoxes = savedTextBoxes.filter((item) => item.pageLanguage === '__page_shared__');
  const languageSpecificTextBoxes = savedTextBoxes.filter((item) => item.pageLanguage !== '__page_shared__');
  const textBoxCount = boxes.length;
  const renderSavedTextBox = (item) => {
    const isMainText = item.role === 'main' || item.sourceType === 'page_main';
    const label = isMainText
      ? 'Main text'
      : pageTextEntries.find((entry) => entry.id === item.sourceTextEntryId)?.label || item.name || 'Text box';
    const entryIndex = isMainText ? -1 : pageTextEntries.findIndex((entry) => entry.id === item.sourceTextEntryId);
    const colors = getTextBoxColor(entryIndex);
    const style = item.style || {};
    const config = style.useCustomLayout ? style.layoutConfig || {} : style.presetConfig || {};
    const assignedPreset = designPresets.find((preset) => preset._id === style.presetId);
    const presetConfig = assignedPreset?.config || style.presetConfig || {};
    const overrides = style.useCustomLayout
      ? ['Own text settings']
      : ['fontFamily', 'fontSize', 'textWidth', 'alignment', 'lineHeight', 'backgroundMode', 'textVisibility', 'padding']
        .filter((key) => config[key] !== undefined && JSON.stringify(config[key]) !== JSON.stringify(presetConfig[key]))
        .map((key) => ({
          fontFamily: 'font', fontSize: 'font size', textWidth: 'text width', alignment: 'alignment',
          lineHeight: 'line spacing', backgroundMode: 'background', textVisibility: 'language display', padding: 'padding',
        }[key]));
    if (item.schemaVersion === 1) overrides.splice(0, overrides.length, ...new Set(Object.keys(item.styleOverrides || {}).map((key) => ({ fontSize: 'font size', fontSizeMode: 'font sizing', fontFamily: 'font', fontFamilySecondary: 'second font', fontStyle: 'font style', fontStyleSecondary: 'second font style', alignment: 'alignment', lineHeight: 'line spacing', padding: 'padding', backgroundMode: 'background', separatorSpacing: 'language spacing', separatorPadding: 'language spacing', separatorHeight: 'divider height', showDivider: 'divider' }[key] || 'custom formatting'))));
    if ((item.layoutConfig?.positionMode || style.positionMode) === 'custom') overrides.push('custom position');
    const boxPresetName = resolveBoxPreset(item, designPresets, editor.bookDefaultPresetId).name;
    const position = item.position || {};
    const isSelected = selectedTextItemId === item.id;
    const selectBox = () => { setSelectedTextItemId(item.id); setSelectedId(null); };
    return (
      <div key={item.id} role="button" tabIndex={0} aria-pressed={isSelected} onClick={selectBox} onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          selectBox();
        }
      }} className={`cursor-pointer rounded-lg border px-3 py-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 ${isSelected ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-600' : colors.card}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 break-words pt-1 font-semibold text-slate-900">{label}{isSelected && <span className="ml-2 inline-block rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-800">Selected</span>}{item.visibleOnCanvas === false && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">Hidden</span>}</p>
          <TextBoxActionsMenu name={label} hidden={item.visibleOnCanvas === false}
            disabled={savingTextItems || editor.savingTextEntries || savingPage || renderingPreview || copyingLayoutTemplate}
            onRename={!isMainText && item.sourceTextEntryId ? () => renamePageTextEntry(item.sourceTextEntryId) : undefined}
            onToggle={() => toggleLayoutBoxVisibility(item.id)}
            onRemove={() => editor.removeLayoutBox(item.id)}
            onDelete={!isMainText && item.sourceTextEntryId ? () => editor.deletePageTextEntry(item.sourceTextEntryId) : undefined} />
        </div>
        <p className="mt-1 text-slate-600"><strong>Text style:</strong> {boxPresetName}</p>
        <p className="mt-1 text-slate-500">
          <strong>Special settings:</strong> {overrides.length ? overrides.join(', ') : 'None'}
          {(item.layoutConfig?.positionMode || style.positionMode) === 'custom' && Number.isFinite(Number(position.x)) && Number.isFinite(Number(position.y))
            ? ` · x ${Math.round(Number(position.x))}, y ${Math.round(Number(position.y))}` : ''}
        </p>

      </div>
    );
  };

  const changeWorkflowTab = (tab) => {
    setActiveWorkflowTab(tab);
  };

  return (
    <aside
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm xl:sticky xl:top-20"
    >
      <WorkflowTabs activeTab={activeWorkflowTab} onChange={changeWorkflowTab} />

      {pageData?.layout_version !== 1 && <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">This older page has not been migrated. Review its boxes before saving; saving makes the current layout explicit, including an empty layout.</p>}

      {activeWorkflowTab === 'page-layout' && (
        <div className="mt-4 space-y-3">
          <section className="rounded-xl border border-violet-200 bg-violet-50/30 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-white p-2 text-violet-800"><Type size={16} /></span>
                <div>
                  <h2 className="text-sm font-bold text-slate-950">Private page layout</h2>
                  <p className="text-[11px] text-slate-600">Saved with this page</p>
                </div>
              </div>
            </div>
            <p className="mt-3 rounded-lg bg-white px-3 py-2 text-xs font-medium text-slate-800">{textBoxCount} {textBoxCount === 1 ? 'text box' : 'text boxes'} in this page’s layout</p>
            {!savedMainText && <button type="button" disabled={addingMainText} onClick={() => addMainTextToPage(activeMainTextVisibility)} className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-violet-300 bg-white px-3 py-2 text-xs font-semibold text-violet-800 disabled:opacity-40"><Plus size={14} />{addingMainText ? 'Adding…' : 'Add main text'}</button>}
            {savedMainText && <div className="mt-2">{renderSavedTextBox(savedMainText)}</div>}
            {sharedTextBoxes.length > 0 && (
              <div className="mt-2 space-y-2">{sharedTextBoxes.map(renderSavedTextBox)}</div>
            )}
            {languageSpecificTextBoxes.length > 0 && (
              <div className="mt-2 space-y-2">{languageSpecificTextBoxes.map(renderSavedTextBox)}</div>
            )}
            {pageData?.vector_layout_id && <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs text-slate-700"><strong>Decorations:</strong> {pageData.vector_layout_name || 'Connected'}</p>}
            <button type="button" onClick={openAdditionalContentModal} className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50"><Plus size={14} /> Add another text box</button>
            <button type="button" onClick={saveLayout} disabled={savingTextItems || addingMainText || savingPage || renderingPreview || copyingLayoutTemplate} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-violet-800 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-900 disabled:cursor-not-allowed disabled:opacity-40">
              <Save size={14} />{savingTextItems ? 'Saving layout…' : 'Save layout'}
            </button>
            <p className="mt-2 text-xs text-slate-500">Saves the text boxes and their formatting on this page.</p>
          </section>
          <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
            <input ref={imageUploadRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" aria-label="Choose an image to add" />
            <button type="button" onClick={() => imageUploadRef.current?.click()} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><ImageIcon size={14} /> Add image</button>
            <button type="button" onClick={editor.addWhiteCover} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Square size={14} /> Add white cover</button>
            <details className="rounded-lg border border-slate-300 bg-white p-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-700">Add saved decorations</summary>
              <select aria-label="Saved decorations" value={selectedVectorLayoutId} onChange={(event) => handleVectorLayoutSelection(event.target.value)} className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs"><option value="">Choose decorations</option>{vectorLayouts.map((layout) => <option key={layout._id} value={layout._id}>{layout.name}</option>)}</select>
              <button type="button" onClick={applyVectorLayout} disabled={!selectedVectorLayoutId} className="mt-2 w-full rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40">Add decorations</button>
            </details>
          </section>
          <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
            <h2 className="text-sm font-bold text-slate-950">Reuse this layout</h2>
            <p className="text-xs text-slate-600">Save the text boxes and formatting for pages with the same dimensions. Each page keeps its own copy.</p>
            {showTemplateForm ? (
              <form onSubmit={async (event) => {
                event.preventDefault();
                if (await saveLayoutTemplate(templateName)) {
                  setTemplateName('');
                  setShowTemplateForm(false);
                }
              }} className="space-y-2">
                <label htmlFor="layout-template-name" className="block text-xs font-semibold text-slate-700">Template name</label>
                <input id="layout-template-name" value={templateName} onChange={(event) => setTemplateName(event.target.value)} disabled={savingLayoutTemplate} required className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs" />
                <div className="flex gap-2">
                  <button type="submit" disabled={!templateName.trim() || savingLayoutTemplate} className="flex-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40">{savingLayoutTemplate ? 'Saving…' : 'Save template'}</button>
                  <button type="button" onClick={() => setShowTemplateForm(false)} disabled={savingLayoutTemplate} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Cancel</button>
                </div>
              </form>
            ) : (
              <button type="button" onClick={() => setShowTemplateForm(true)} disabled={copyingLayoutTemplate} className="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40">Save as template</button>
            )}
            <details open className="border-t border-slate-200 pt-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-800">Copy a template to this page</summary>
              <label htmlFor="page-layout-template" className="mt-3 block text-xs font-semibold text-slate-700">Compatible layout templates</label>
              <select id="page-layout-template" value={selectedLayoutTemplateId} onChange={(event) => setSelectedLayoutTemplateId(event.target.value)} disabled={loadingLayoutTemplates || copyingLayoutTemplate || savingLayoutTemplate || layoutTemplates.length === 0} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs disabled:opacity-40">
                <option value="">{loadingLayoutTemplates ? 'Loading templates…' : layoutTemplates.length ? 'Choose a template' : 'No compatible templates'}</option>
                {layoutTemplates.map((template) => <option key={template._id} value={template._id}>{template.name}</option>)}
              </select>
              <a href="/layout-templates" target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline" title="Open layout templates in a new tab">View all templates <ExternalLink size={12} aria-hidden="true" /></a>
              <p className="mt-2 text-xs text-slate-500">Templates can come from any book. Copying gives this page its own layout and keeps its wording.</p>
              {layoutTemplatesError && <p role="alert" className="mt-2 text-xs text-red-700">{layoutTemplatesError}</p>}
              {!loadingLayoutTemplates && !layoutTemplatesError && layoutTemplates.length === 0 && <p className="mt-2 text-xs text-slate-500">No templates match this page’s dimensions.</p>}
              <button type="button" onClick={copyLayoutTemplate} disabled={!selectedLayoutTemplateId || loadingLayoutTemplates || copyingLayoutTemplate || savingLayoutTemplate} className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40">{copyingLayoutTemplate ? 'Copying…' : 'Copy to private layout'}</button>
            </details>
          </section>
        </div>
      )}

      {activeWorkflowTab === 'output' && <div className="mt-4 space-y-3">
        <h2 className="text-sm font-bold text-slate-950">Finish this page</h2>
        <p className="text-xs text-slate-500">Render the current page, then download it if needed.</p>
        <button type="button" onClick={savePage} disabled={savingPage || renderingPreview || editor.removingRenderedPage} className="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40">{savingPage || renderingPreview ? 'Rendering…' : 'Render page'}</button>
        <button type="button" onClick={exportToPDF} className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Download size={14} /> Download current page as PDF</button>
      </div>}
    </aside>
  );
}
